import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import API from '../api/Api';
import { LocalStorage } from "../components";
import initialState from "../constants/initialState";
import storageType from "../constants/storageType";
import i18n from '../i18n';

// ── Bill computation ────────────────────────────────────────────────────────
// Mirror of node/services/tax.service.computeBill and customer Cart.js computeBill.
// Display only — server recomputes authoritatively on payBill.
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const computeBill = (taxConfig, subtotal) => {
    const sub = round2(subtotal);
    const mode = taxConfig?.tax_mode || 'none';
    const rate = Number(taxConfig?.tax_rate) || 0;
    let tax = 0;
    if (rate > 0 && mode === 'inclusive') tax = round2(sub - sub / (1 + rate / 100));
    else if (rate > 0 && mode === 'exclusive') tax = round2(sub * rate / 100);
    const lines = (taxConfig?.charges || []).map(c => ({
        name: c.name,
        amount: round2(c.type === 'fixed' ? Number(c.value) : sub * Number(c.value) / 100),
    }));
    const chargesTotal = round2(lines.reduce((s, c) => s + c.amount, 0));
    const total = round2(sub + (mode === 'exclusive' ? tax : 0) + chargesTotal);
    return { sub, mode, rate, label: taxConfig?.tax_label || i18n.t('pos.tax_label'), tax, lines, total };
};

// Define initial state
const initState = {
    language: initialState.language,
    isFetching: initialState.isFetching,
    isSuccess: initialState.isSuccess,
    isError: initialState.isError,
    errorMessage: initialState.errorMessage,
    topbar: {
        // Trimmed to the counter-ordering flow only (Rajat 2026-07-03): "New Orders" duplicated the
        // /owner/orders board, and Customer/Location/Cash Sales/Insert Amount were unbuilt placeholders.
        // A future "regular customers" directory (for quick delivery) is worth building on its own.
        default: 'cart',
        buttons: [
            { key: 'cart', title: 'Cart', faicon: 'FaShoppingCart', is_active: true },
        ],
    },
    category_id: LocalStorage.getValue(storageType.CATEGORY_ID) || 0,
    venueItems: [],
    newOrder: [],
    venueTaxConfig: null, // loaded once per POS session from GET /venues/tax-config/:id
    cart: {
        order_no: LocalStorage.getValue(storageType.ACTIVE_ORDER_NO) || 0,
        items: LocalStorage.getValue(storageType.CART_ITEMS) || [],
        quantity: 0,
        discount: 0,
        sub_total: 0,
        tax_total: 0,
        grand_total: 0,
        // bill breakdown (mirrors computeBill output) — for display in CartItems
        bill: null,
    },
};

// Get menu items by Venue & Category
export const getMenuItemByVenueCategory = createAsyncThunk(
    'pos-manage/menu-item-by-venue-category',
    async (params, thunkAPI) => {
        try {
            // Append category id
            let categoryID = LocalStorage.getValue(storageType.CATEGORY_ID) || 0;
            params['category_id'] = categoryID;
            const result = await API.getData('venue-category/menu-items', params);
            const response = result.data;
            if (result.data) {
                const res = response.data;
                if (parseInt(categoryID) > 0) {
                    res['category'] = LocalStorage.getValue(storageType.CATEGORY_ARR);
                } else {
                    LocalStorage.setValue(storageType.CATEGORY_ARR, res.category);
                }
                return res;
            } else {
                return thunkAPI.rejectWithValue({ error: response.message });
            }
        } catch (err) {
            toast.error(err.message);
            return thunkAPI.rejectWithValue({ error: err.message });
        }
    }
);

// Fetch the venue's tax + charges config once per POS session.
// Mirrors what the customer Cart.js does (GET /venues/tax-config/:venueId).
export const fetchVenueTaxConfig = createAsyncThunk(
    'pos-manage/fetch-venue-tax-config',
    async (venueId, thunkAPI) => {
        try {
            const result = await API.getData(`venues/tax-config/${venueId}`);
            if (result.data?.data) return result.data.data;
            return null;
        } catch {
            return null; // non-fatal — POS continues without venue tax; falls back to no-tax display
        }
    }
);

// Pay Bill — create a real order from the current POS cart (in-store / walk-in sale)
export const payBill = createAsyncThunk(
    'pos-manage/pay-bill',
    async ({ venue_id, menu_id, table_no, customer }, thunkAPI) => {
        try {
            const state = thunkAPI.getState().posmanage;
            const cart = state.cart;
            if (!cart.items || cart.items.length === 0) {
                toast.error(i18n.t('pos.toast_add_items_first'));
                return thunkAPI.rejectWithValue({ error: 'empty cart' });
            }
            // Send modifiers as option_id array; server validates + reprices authoritatively.
            // price/pay_amount are omitted — the server is the price source of truth.
            const cart_item = cart.items.map(i => ({
                id: i.id,
                quantity: i.quantity,
                modifiers: (i.selectedModifiers || []).map(m => m.option_id),
            }));
            const bill = state.cart.bill;
            const result = await API.postData('order/pos-bill', {
                menu_id: menu_id || 0,
                // A picked regular attaches their name + mobile + delivery address; otherwise walk-in.
                customer_name: (customer && customer.name) || 'Walk-in (POS)',
                customer_mobile: (customer && customer.mobile) || null,
                customer_address: (customer && customer.address) || null,
                table_no: table_no || '',
                payment_method: 2, // 2 = cash / in-store
                item_count: cart.quantity,
                total_price: parseFloat((bill ? bill.total : cart.grand_total).toFixed(2)),
                cart_item,
            });
            if (result.data && result.data.status) {
                toast.success(i18n.t('pos.toast_bill_paid'));
                return result.data;
            }
            toast.error(result.data?.message || i18n.t('pos.toast_could_not_create_order'));
            return thunkAPI.rejectWithValue({ error: result.data?.message });
        } catch (err) {
            toast.error(i18n.t('pos.toast_could_not_create_order'));
            return thunkAPI.rejectWithValue({ error: err.message });
        }
    }
);

const POSManage = createSlice({
    name: 'pos-manage',
    initialState: initState,
    reducers: {
        clearState(state) {
            Object.assign(state, initState);
        },
        handleTopbarButton(state, { payload }) {
            state.topbar.default = payload.key;
            state.topbar.buttons.map(item => {
                if (item.key === payload.key) {
                    item.is_active = true;
                } else {
                    item.is_active = false;
                }
                return item;
            });
        },
        setCategoryID(state, { payload }) {
            state.category_id = payload;
            LocalStorage.setValue(storageType.CATEGORY_ID, payload);
        },
        // addToCart: dedup by dish.id + sorted option ids.
        // Caller passes { ...item, selectedModifiers: [...], _cartKey: '...' }
        // (ProductList sets _cartKey and selectedModifiers before dispatching).
        addToCart(state, { payload }) {
            const cartItems = state.cart.items;
            // Compute fingerprint: use pre-computed _cartKey if available, or plain id for
            // no-modifier items added by the legacy path (backward-compatible).
            const key = payload._cartKey || String(payload.id);
            const itemIndex = cartItems.findIndex(item => (item._cartKey || String(item.id)) === key);
            if (itemIndex >= 0) {
                cartItems[itemIndex].quantity += 1;
                cartItems[itemIndex].total_amount = round2(cartItems[itemIndex].price * cartItems[itemIndex].quantity);
                toast.success(i18n.t('pos.toast_increased_qty', { name: cartItems[itemIndex].lang_en }),
                    { position: 'bottom-left' }
                );
            } else {
                const effectivePrice = payload.effectivePrice != null
                    ? round2(payload.effectivePrice)
                    : round2(parseFloat(payload.price || 0));
                const newItem = {
                    ...payload,
                    price: effectivePrice,
                    quantity: 1,
                    total_amount: effectivePrice,
                    _cartKey: key,
                    selectedModifiers: payload.selectedModifiers || [],
                };
                cartItems.push(newItem);
                toast.success(i18n.t('pos.toast_added_to_cart', { name: payload.lang_en }), { position: 'bottom-left' });
            }
            LocalStorage.setValue(storageType.CART_ITEMS, cartItems);
            Object.assign(state.cart, { items: cartItems });
        },
        removeFromCart(state, { payload }) {
            const cartItems = state.cart.items;
            const key = payload._cartKey || String(payload.id);
            const nextItems = cartItems.filter(item => (item._cartKey || String(item.id)) !== key);
            LocalStorage.setValue(storageType.CART_ITEMS, nextItems);
            toast.error(i18n.t('pos.toast_removed_from_cart', { name: payload.lang_en }), { position: 'bottom-left' });
            Object.assign(state.cart, { items: nextItems });
        },
        decreasedCartQuantity(state, { payload }) {
            let cartItems = state.cart.items;
            const key = payload._cartKey || String(payload.id);
            const itemIndex = cartItems.findIndex(item => (item._cartKey || String(item.id)) === key);
            if (itemIndex >= 0) {
                if (cartItems[itemIndex].quantity > 1) {
                    cartItems[itemIndex].quantity -= 1;
                    cartItems[itemIndex].total_amount = round2(cartItems[itemIndex].price * cartItems[itemIndex].quantity);
                    toast.success(i18n.t('pos.toast_decreased_qty', { name: cartItems[itemIndex].lang_en }), { position: 'bottom-left' });
                } else {
                    cartItems = cartItems.filter(item => (item._cartKey || String(item.id)) !== key);
                    toast.error(i18n.t('pos.toast_removed_from_cart', { name: payload.lang_en }), { position: 'bottom-left' });
                }
                LocalStorage.setValue(storageType.CART_ITEMS, cartItems);
                Object.assign(state.cart, { items: cartItems });
            }
        },
        // getCartTotal: recomputes totals using the venue's real tax config.
        // Falls back to no-tax (mode='none') if venueTaxConfig is null.
        getCartTotal(state) {
            const cartItems = state.cart.items;
            let quantity = 0;
            let sub_total = 0;
            cartItems.forEach(item => {
                quantity += item.quantity;
                sub_total = round2(sub_total + item.total_amount);
            });
            const bill = computeBill(state.venueTaxConfig, sub_total);
            Object.assign(state.cart, {
                quantity,
                discount: 0,
                sub_total: bill.sub,
                tax_total: bill.tax,
                grand_total: bill.total,
                bill,
            });
        },
        clearCart(state) {
            Object.assign(state.cart, { items: [], bill: null });
            toast.error(i18n.t('pos.toast_order_cancelled'), { position: 'bottom-left' });
            LocalStorage.setValue(storageType.CART_ITEMS, []);
        },
        holdOrder(state, { payload }) {
            const cart = state.cart;
            state.newOrder.push({
                order_id: payload,
                order_items: cart.items
            });
            // Clear cart
            Object.assign(state.cart, { items: [], bill: null });
            toast.error(i18n.t('pos.toast_hold_order'), { position: 'bottom-left' });
            LocalStorage.setValue(storageType.CART_ITEMS, []);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getMenuItemByVenueCategory.pending, (state) => {
                state.isFetching = true;
            })
            .addCase(getMenuItemByVenueCategory.fulfilled, (state, { payload }) => {
                state.isFetching = false;
                state.isError = false;
                state.errorMessage = '';
                state.isSuccess = true;
                state.venueItems = payload;
            })
            .addCase(getMenuItemByVenueCategory.rejected, (state, { payload }) => {
                let errMsg = payload.error || payload.message;
                toast.error(errMsg);
                state.isFetching = false;
                state.isError = true;
                state.errorMessage = errMsg;
            })
            .addCase(fetchVenueTaxConfig.fulfilled, (state, { payload }) => {
                state.venueTaxConfig = payload;
            })
            .addCase(payBill.fulfilled, (state) => {
                // clear cart after a successful sale
                Object.assign(state.cart, {
                    items: [], quantity: 0, sub_total: 0, tax_total: 0, grand_total: 0, bill: null,
                });
                LocalStorage.setValue(storageType.CART_ITEMS, []);
            });
    }
});

export const {
    clearState,
    handleTopbarButton,
    setCategoryID,
    addToCart,
    removeFromCart,
    decreasedCartQuantity,
    getCartTotal,
    clearCart,
    holdOrder,
} = POSManage.actions;
export default POSManage.reducer;
